import PropTypes from 'prop-types';
import {
  Box,
  Chip,
  CircularProgress,
  FormControl,
  InputAdornment,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { AutoGraph, Search } from '@mui/icons-material';
import { alpha } from '@mui/material/styles';

import { colors, fontSize, getScoreBg, getScoreColor } from '../../theme';
import {
  Card,
  CardTitle,
  filterControlSx,
  SectionLabel,
  tdSx,
  thSx,
  trSx,
} from './primitives';
import {
  CONFIDENCE_OPTIONS,
  SIGNAL_TYPE_OPTIONS,
  fmtNumber,
  fmtPrice,
  getConfidenceLabel,
  getLocalizedText,
  getSignalTypeLabel,
  getStateLabel,
  stateColor,
} from './shared';

const rowShape = PropTypes.shape({
  ticker: PropTypes.string,
  score_date: PropTypes.string,
  state: PropTypes.string,
  state_label: PropTypes.shape({
    vi: PropTypes.string,
    en: PropTypes.string,
  }),
  score: PropTypes.number,
  score_level: PropTypes.string,
  confidence: PropTypes.string,
  confidence_label: PropTypes.shape({
    vi: PropTypes.string,
    en: PropTypes.string,
  }),
  signal_type: PropTypes.string,
  signal_type_label: PropTypes.shape({
    vi: PropTypes.string,
    en: PropTypes.string,
  }),
  daily: PropTypes.shape({
    close: PropTypes.number,
  }),
  signal: PropTypes.shape({
    entry: PropTypes.number,
    stop: PropTypes.number,
    rr_ratio: PropTypes.number,
    trigger: PropTypes.string,
    trigger_i18n: PropTypes.shape({
      vi: PropTypes.string,
      en: PropTypes.string,
    }),
  }),
});

const numericTdSx = {
  ...tdSx,
  fontFamily: 'monospace',
  fontWeight: 600,
  textAlign: 'right',
  whiteSpace: 'nowrap',
};

function RankingsTable({
  loading,
  filteredRows,
  onOpenTicker,
  t,
  language,
  source,
  onSourceChange,
  scoreDate,
  onScoreDateChange,
  minScore,
  onMinScoreChange,
  stateCounts,
  stateFilter,
  onStateFilterChange,
  confidence,
  onConfidenceChange,
  signalType,
  onSignalTypeChange,
  search,
  onSearchChange,
}) {
  const headers = [
    { key: 'ticker', label: t('ichimokuTransition.table.ticker'), align: 'left' },
    { key: 'state', label: t('ichimokuTransition.table.state'), align: 'left' },
    { key: 'score', label: t('ichimokuTransition.table.score'), align: 'center' },
    { key: 'confidence', label: t('ichimokuTransition.table.confidence'), align: 'left', hide: 'md' },
    { key: 'signalType', label: t('ichimokuTransition.table.signalType'), align: 'left', hide: 'lg' },
    { key: 'close', label: t('ichimokuTransition.table.close'), align: 'right' },
    { key: 'entry', label: t('ichimokuTransition.table.entry'), align: 'right', hide: 'md' },
    { key: 'stop', label: t('ichimokuTransition.table.stop'), align: 'right', hide: 'md' },
    { key: 'rr', label: t('ichimokuTransition.table.rr'), align: 'right' },
    { key: 'trigger', label: t('ichimokuTransition.table.trigger'), align: 'left', hide: 'xl' },
  ];

  const stateItems = Object.entries(stateCounts).sort((a, b) => b[1] - a[1]);
  const total = stateItems.reduce((sum, [, count]) => sum + count, 0);

  const headerAction = loading ? (
    <CircularProgress size={18} />
  ) : (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
      <ToggleButtonGroup
        value={source}
        exclusive
        onChange={(_, value) => value && onSourceChange(value)}
        size="small"
        sx={{
          bgcolor: colors.gray[50],
          p: 0.25,
          borderRadius: 1,
          '& .MuiToggleButton-root': {
            height: 32,
            px: 1.25,
            textTransform: 'none',
            fontWeight: 700,
            fontSize: fontSize.sm,
            border: 'none',
            borderRadius: '6px !important',
            color: colors.gray[500],
            '&.Mui-selected': {
              bgcolor: colors.background.paper,
              color: colors.info.main,
              boxShadow: '0 1px 3px rgba(15,23,42,0.08)',
            },
          },
        }}
      >
        <ToggleButton value="persisted">{t('ichimokuTransition.persisted')}</ToggleButton>
        <ToggleButton value="live">{t('ichimokuTransition.live')}</ToggleButton>
      </ToggleButtonGroup>
      <TextField
        size="small"
        placeholder={t('ichimokuTransition.filters.search')}
        value={search}
        onChange={(event) => onSearchChange(event.target.value.toUpperCase())}
        sx={{
          width: { xs: 140, sm: 168 },
          '& .MuiOutlinedInput-root': {
            height: 32,
            fontSize: fontSize.sm,
            bgcolor: colors.background.paper,
          },
        }}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ fontSize: 16, color: colors.gray[400] }} />
              </InputAdornment>
            ),
          },
        }}
      />
      <Chip
        label={`${filteredRows.length}/${total}`}
        size="small"
        sx={{ fontWeight: 700, bgcolor: colors.gray[50], color: colors.gray[700], height: 28 }}
      />
    </Box>
  );

  return (
    <Card>
      <CardTitle icon={AutoGraph} action={headerAction}>
        {t('ichimokuTransition.table.title')}
      </CardTitle>

      <Box sx={{ px: 2, py: 1.5, borderBottom: `1px solid ${colors.gray[100]}`, bgcolor: colors.gray[50] }}>
        <SectionLabel>{t('ichimokuTransition.snapshot.title')}</SectionLabel>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, alignItems: 'center' }}>
          <Chip
            label={`${t('ichimokuTransition.filters.all')} ${total}`}
            clickable
            onClick={() => onStateFilterChange('ALL')}
            variant={stateFilter === 'ALL' ? 'filled' : 'outlined'}
            sx={{
              height: 28,
              fontWeight: 800,
              fontSize: fontSize.sm,
              bgcolor: stateFilter === 'ALL' ? colors.gray[800] : colors.background.paper,
              color: stateFilter === 'ALL' ? '#fff' : colors.gray[700],
              borderColor: colors.gray[300],
            }}
          />
          {stateItems.map(([state, count]) => (
            <Chip
              key={state}
              clickable
              onClick={() => onStateFilterChange(stateFilter === state ? 'ALL' : state)}
              label={`${getStateLabel(t, state)} ${count}`}
              variant={stateFilter === state ? 'filled' : 'outlined'}
              sx={{
                height: 28,
                fontSize: fontSize.sm,
                fontWeight: 800,
                bgcolor: stateFilter === state ? `${stateColor(state)}18` : colors.background.paper,
                color: stateColor(state),
                borderColor: `${stateColor(state)}40`,
                '& .MuiChip-label': { px: 1.05 },
              }}
            />
          ))}
        </Box>
      </Box>

      <Box
        sx={{
          px: 2,
          py: 1.5,
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, minmax(0, 1fr))',
            md: 'repeat(4, minmax(0, 1fr))',
          },
          gap: 1,
          borderBottom: `1px solid ${colors.gray[100]}`,
        }}
      >
        {source === 'persisted' ? (
          <TextField
            size="small"
            type="date"
            label={t('ichimokuTransition.filters.scoreDate')}
            value={scoreDate}
            onChange={(event) => onScoreDateChange(event.target.value)}
            slotProps={{ inputLabel: { shrink: true } }}
            sx={filterControlSx}
          />
        ) : null}

        <TextField
          size="small"
          type="number"
          label={t('ichimokuTransition.filters.minScore')}
          value={minScore}
          onChange={(event) => onMinScoreChange(Number(event.target.value) || 0)}
          slotProps={{ inputLabel: { shrink: true }, htmlInput: { min: 0, max: 100 } }}
          sx={filterControlSx}
        />

        <FormControl size="small" sx={filterControlSx}>
          <Select
            value={confidence}
            displayEmpty
            onChange={(event) => onConfidenceChange(event.target.value)}
            sx={{ fontSize: fontSize.sm }}
          >
            <MenuItem value="ALL">
              {t('ichimokuTransition.filters.confidence')}: {t('ichimokuTransition.filters.all')}
            </MenuItem>
            {CONFIDENCE_OPTIONS.map((item) => (
              <MenuItem key={item} value={item}>{getConfidenceLabel(t, item)}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={filterControlSx}>
          <Select
            value={signalType}
            displayEmpty
            onChange={(event) => onSignalTypeChange(event.target.value)}
            sx={{ fontSize: fontSize.sm }}
          >
            <MenuItem value="ALL">
              {t('ichimokuTransition.filters.signalType')}: {t('ichimokuTransition.filters.all')}
            </MenuItem>
            {SIGNAL_TYPE_OPTIONS.map((item) => (
              <MenuItem key={item} value={item}>{getSignalTypeLabel(t, item)}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              {headers.map(({ key, label, align, hide }) => (
                <TableCell
                  key={key}
                  align={align}
                  sx={{
                    ...thSx,
                    display: hide ? { xs: 'none', [hide]: 'table-cell' } : undefined,
                  }}
                >
                  {label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredRows.map((row) => (
              <TableRow
                key={`${row.ticker}-${row.score_date || 'live'}`}
                hover
                sx={trSx}
                onClick={() => onOpenTicker(row.ticker)}
              >
                <TableCell sx={{ ...tdSx, fontFamily: 'monospace', fontWeight: 800, color: colors.gray[800] }}>
                  {row.ticker}
                </TableCell>
                <TableCell sx={tdSx}>
                  <Chip
                    label={getLocalizedText(row.state_label, language, getStateLabel(t, row.state))}
                    size="small"
                    sx={{
                      bgcolor: `${stateColor(row.state)}14`,
                      color: stateColor(row.state),
                      fontWeight: 700,
                      height: 22,
                      fontSize: fontSize.xs,
                    }}
                  />
                </TableCell>
                <TableCell sx={{ ...tdSx, textAlign: 'center' }}>
                  <Chip
                    label={fmtNumber(row.score, 1)}
                    size="small"
                    sx={{
                      bgcolor: getScoreBg(row.score_level),
                      color: getScoreColor(row.score_level),
                      fontWeight: 800,
                      minWidth: 44,
                      height: 22,
                    }}
                  />
                </TableCell>
                <TableCell sx={{ ...tdSx, display: { xs: 'none', md: 'table-cell' }, fontWeight: 600 }}>
                  {getLocalizedText(row.confidence_label, language, getConfidenceLabel(t, row.confidence))}
                </TableCell>
                <TableCell sx={{ ...tdSx, display: { xs: 'none', lg: 'table-cell' }, color: colors.gray[600] }}>
                  {getLocalizedText(row.signal_type_label, language, getSignalTypeLabel(t, row.signal_type))}
                </TableCell>
                <TableCell sx={numericTdSx}>{fmtPrice(row.daily?.close)}</TableCell>
                <TableCell sx={{ ...numericTdSx, display: { xs: 'none', md: 'table-cell' } }}>
                  {fmtPrice(row.signal?.entry)}
                </TableCell>
                <TableCell sx={{ ...numericTdSx, display: { xs: 'none', md: 'table-cell' }, color: colors.error.main }}>
                  {fmtPrice(row.signal?.stop)}
                </TableCell>
                <TableCell
                  sx={{
                    ...numericTdSx,
                    color: (row.signal?.rr_ratio || 0) >= 1.5 ? colors.success.main : colors.gray[700],
                  }}
                >
                  {fmtNumber(row.signal?.rr_ratio, 2)}
                </TableCell>
                <TableCell
                  sx={{
                    ...tdSx,
                    display: { xs: 'none', xl: 'table-cell' },
                    maxWidth: 280,
                    color: colors.gray[500],
                  }}
                >
                  <Tooltip title={getLocalizedText(row.signal?.trigger_i18n, language, row.signal?.trigger || '—')}>
                    <Typography sx={{ fontSize: fontSize.sm, ...{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } }}>
                      {getLocalizedText(row.signal?.trigger_i18n, language, row.signal?.trigger || '—')}
                    </Typography>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {!loading && !filteredRows.length ? (
        <Box
          sx={{
            px: 2,
            py: 5,
            textAlign: 'center',
            borderTop: `1px dashed ${colors.gray[200]}`,
            bgcolor: alpha(colors.gray[50], 0.5),
          }}
        >
          <Typography sx={{ fontSize: fontSize.md, color: colors.gray[500], fontWeight: 600 }}>
            {t('ichimokuTransition.table.empty')}
          </Typography>
        </Box>
      ) : null}
    </Card>
  );
}

RankingsTable.propTypes = {
  loading: PropTypes.bool.isRequired,
  filteredRows: PropTypes.arrayOf(rowShape).isRequired,
  onOpenTicker: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
  language: PropTypes.string.isRequired,
  source: PropTypes.string.isRequired,
  onSourceChange: PropTypes.func.isRequired,
  scoreDate: PropTypes.string.isRequired,
  onScoreDateChange: PropTypes.func.isRequired,
  minScore: PropTypes.number.isRequired,
  onMinScoreChange: PropTypes.func.isRequired,
  stateCounts: PropTypes.objectOf(PropTypes.number).isRequired,
  stateFilter: PropTypes.string.isRequired,
  onStateFilterChange: PropTypes.func.isRequired,
  confidence: PropTypes.string.isRequired,
  onConfidenceChange: PropTypes.func.isRequired,
  signalType: PropTypes.string.isRequired,
  onSignalTypeChange: PropTypes.func.isRequired,
  search: PropTypes.string.isRequired,
  onSearchChange: PropTypes.func.isRequired,
};

export default RankingsTable;
